import { openKlaus, trackKlausImpression } from '@app/constants/klaus';
import { useNavigation } from '@app/hooks/useNavigation';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  /** AI services the company declared in the questionnaire — never empty when this renders. */
  tools: string[];
}

/**
 * Contextual Klaus cross-promo above a finished privacy policy, shown when the questionnaire
 * revealed that the company uses AI services (see `useKlausAiSignal`). Screen only — the callers'
 * guards keep it out of print and PDF output.
 */
export function KlausPanel({ tools }: Props) {
  const { t } = useTranslation();
  const { setNavigation } = useNavigation();

  useEffect(() => {
    trackKlausImpression('context-panel');
  }, []);

  const toolList =
    tools.length === 1
      ? tools[0]
      : `${tools.slice(0, -1).join(', ')} ${t('klaus.panel.and')} ${tools[tools.length - 1]}`;

  return (
    <div
      className="bg-secondary-50 border-l-4 border-secondary-root px-5 py-4 my-4 mx-auto w-full max-w-[800px] relative"
      data-testid="klaus-context-panel"
    >
      <button
        onClick={() => setNavigation((nav) => void (nav.klausPanelDismissed = true))}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        title={t('klaus.panel.dismiss')}
        data-testid="klaus-panel-dismiss"
      >
        <CloseIcon fontSize="small" />
      </button>

      <div className="flex items-center gap-3">
        <img src="/assets/images/klaus/klaus-logo.svg" alt="Klaus kann KI" className="h-[32px]" />
        <span className="text-xs uppercase tracking-wide text-gray-500">{t('klaus.panel.kicker')}</span>
      </div>

      <p className="mt-3 font-semibold">
        <Trans t={t} i18nKey="klaus.panel.tools" values={{ tools: toolList }} />
      </p>
      <p className="mt-1 text-sm">{t('klaus.panel.body')}</p>

      <button
        onClick={() => openKlaus('context-panel')}
        className="mt-3 text-sm font-medium text-primary-root hover:underline"
        data-testid="klaus-panel-cta"
      >
        {t('klaus.panel.cta')} →
      </button>
      <div className="mt-2 text-xs text-gray-500">{t('klaus.panel.meta')}</div>
    </div>
  );
}
